import React, { useEffect, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

//Jds
import {
  Button,
  Container,
  Icon,
  InputToggle,
  Notifications,
  Skeleton,
  Text,
} from "@jds/core";

//css
import classes from "./CommonLayout.module.css";
// Mui
import { Box, FormControlLabel, Stack, Switch } from "@mui/material";
//img
// import invitationImg from "../../assets/png/invitation-img.png";
// Local
import EventBanner from "../UI/EventBanner";
import EventDescriptionCard from "../UI/Cards/EventDescriptionCard";
import useApiHttp from "../../hooks/use-http";
import {
  GOOGLE_MAP_API_KEY,
  addParamsURL,
  baseToImg,
  downloadQrImage,
} from "../../util/helper";
import { useDispatch } from "react-redux";
import { setSidebarDetails } from "../../store/state-slices/eventDetails";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import PageLoader from "../UI/Loader/PageLoader";
import JdsModal from "../UI/JdsModal";
import decryptUrl from "../../util/img-decryption";

const libraries = ["places"];

const A4_WIDTH = 595.28;
const A4_HEIGHT = 841.89;

const getInvitationFileName = (eventName, extension) => {
  const safeName = (eventName || "invitation")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `${safeName || "invitation"}-invitation.${extension}`;
};

const getEventRegistrationLink = (eventId) => {
  const baseUrl =
    process.env.REACT_APP_JIOCLOUD_LANDING_URL || window.location.origin;

  return `${baseUrl}/events/sign-up?event_id=${eventId}`;
};

const escapePdfString = (value) =>
  `${value || ""}`
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const loadExportImage = (src) => {
  if (!src) return Promise.resolve(null);

  return new Promise((resolve) => {
    const image = new Image();
    if (!src.startsWith("data:")) {
      image.crossOrigin = "anonymous";
    }
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
};

const wrapText = (context, text, maxWidth) => {
  const words = `${text || ""}`.split(" ");
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const nextLine = line ? `${line} ${word}` : word;
    if (context.measureText(nextLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = nextLine;
    }
  });

  if (line) lines.push(line);
  return lines;
};

const drawWrappedText = (context, text, x, y, maxWidth, lineHeight) => {
  const lines = wrapText(context, text, maxWidth);
  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * lineHeight);
  });

  return y + lines.length * lineHeight;
};

const drawCoverImage = (context, image, x, y, width, height) => {
  const scale = Math.max(width / image.width, height / image.height);
  const sourceWidth = width / scale;
  const sourceHeight = height / scale;
  const sourceX = (image.width - sourceWidth) / 2;
  const sourceY = (image.height - sourceHeight) / 2;

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height,
  );
};

const drawLinkIcon = (context, x, y, size = 24) => {
  context.save();
  context.translate(x + size / 2, y + size / 2);
  context.rotate(-Math.PI / 4);
  context.strokeStyle = "#0b4f71";
  context.lineWidth = 3;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.beginPath();
  context.roundRect(
    -size * 0.44,
    -size * 0.17,
    size * 0.54,
    size * 0.34,
    size * 0.16,
  );
  context.stroke();
  context.beginPath();
  context.roundRect(
    -size * 0.1,
    -size * 0.17,
    size * 0.54,
    size * 0.34,
    size * 0.16,
  );
  context.stroke();
  context.restore();
};

const canvasToJpgBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
        return;
      }

      reject(new Error("Unable to create invitation image."));
    }, "image/jpeg", 0.95);
  });

const createInvitationJpgBlob = async (eventDetails) => {
  const width = 900;
  const padding = 48;
  const contentWidth = width - padding * 2;
  const bannerHeight = 260;
  const qrSize = 190;
  const scale = Math.min(window.devicePixelRatio || 2, 3);
  const bannerUrl = eventDetails?.bannerimage
    ? addParamsURL(decryptUrl(eventDetails.bannerimage), { size: "s" })
    : "";
  const eventLink = getEventRegistrationLink(eventDetails?.id);
  const eventLinkLabel = eventDetails?.name || "Open event";
  const [bannerImage, qrImage] = await Promise.all([
    loadExportImage(bannerUrl),
    loadExportImage(baseToImg(eventDetails?.qrCodeImage)),
  ]);
  const measurementCanvas = document.createElement("canvas");
  const measurementContext = measurementCanvas.getContext("2d");
  let y = padding + bannerHeight + 50;

  measurementContext.font = "700 42px Arial";
  y += 58;
  measurementContext.font = "500 26px Arial";
  y = drawWrappedText(
    measurementContext,
    `We are thrilled to extend an exclusive invitation to you for ${eventDetails?.name} via JioAICloud, your personalized event companion. Your presence would add immense value to the event, and we genuinely hope you can join us.`,
    padding,
    y,
    contentWidth,
    40,
  );
  measurementContext.font = "500 24px Arial";
  const eventLinkLines = wrapText(
    measurementContext,
    eventLinkLabel,
    contentWidth - 136,
  );
  y += 48 + 160 + 64 + qrSize + 48 + eventLinkLines.length * 34 + 36;

  measurementContext.font = "500 24px Arial";
  y = drawWrappedText(
    measurementContext,
    "Note: By confirming your presence, you will gain access to all event-related photos presentations, and exclusive content on JioAICloud.",
    padding,
    y,
    contentWidth,
    36,
  );

  const height = y + padding;
  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;

  const context = canvas.getContext("2d");
  context.scale(scale, scale);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);

  if (bannerImage) {
    drawCoverImage(
      context,
      bannerImage,
      padding,
      padding,
      contentWidth,
      bannerHeight,
    );
  } else {
    context.fillStyle = "#f3f5f7";
    context.fillRect(padding, padding, contentWidth, bannerHeight);
    context.fillStyle = "#0b4f71";
    context.font = "700 36px Arial";
    context.fillText(
      eventDetails?.name || "Event Invitation",
      padding + 32,
      padding + 136,
    );
  }

  y = padding + bannerHeight + 50;
  context.fillStyle = "#141414";
  context.font = "700 42px Arial";
  context.fillText("You're Invited!", padding, y);

  y += 58;
  context.font = "500 26px Arial";
  y = drawWrappedText(
    context,
    `We are thrilled to extend an exclusive invitation to you for ${eventDetails?.name} via JioAICloud, your personalized event companion. Your presence would add immense value to the event, and we genuinely hope you can join us.`,
    padding,
    y,
    contentWidth,
    40,
  );

  y += 48;
  context.font = "700 28px Arial";
  context.fillText("Event Details:", padding, y);
  y += 28;

  context.strokeStyle = "#d7d7d7";
  context.lineWidth = 2;
  context.strokeRect(padding, y, contentWidth, 150);
  context.font = "700 24px Arial";
  const dateLabel =
    eventDetails?.endDate === eventDetails?.startDate ? "Date" : "From";
  const timeLabel =
    eventDetails?.endDate === eventDetails?.startDate ? "Time" : "To";
  const detailRows = [
    ["Event Name", eventDetails?.name],
    [dateLabel, eventDetails?.startDate],
    [
      timeLabel,
      eventDetails?.endDate === eventDetails?.startDate
        ? eventDetails?.time
        : eventDetails?.endDate,
    ],
    ["Venue", eventDetails?.locationName],
  ];

  detailRows.forEach(([label, value], index) => {
    const rowY = y + 38 + index * 32;
    context.fillText(label, padding + 24, rowY);
    context.font = "500 24px Arial";
    context.fillText(`: ${value || ""}`, padding + 220, rowY);
    context.font = "700 24px Arial";
  });

  y += 200;
  context.font = "700 28px Arial";
  context.fillText("Register QR:", padding, y);
  y += 28;

  if (qrImage) {
    context.drawImage(qrImage, (width - qrSize) / 2, y, qrSize, qrSize);
  }

  y += qrSize + 48;
  context.font = "700 24px Arial";
  context.fillText("Event Link:", padding, y);
  context.fillStyle = "#0b4f71";
  context.font = "500 24px Arial";
  const linkTextY = y;
  const linkLines = wrapText(context, eventLinkLabel, contentWidth - 136);
  linkLines.forEach((line, index) => {
    context.fillText(line, padding + 136, y + index * 34);
  });
  const lastLine = linkLines[linkLines.length - 1] || "";
  const iconX = Math.min(
    padding + 136 + context.measureText(lastLine).width + 8,
    width - padding - 20,
  );
  const iconY = y + (linkLines.length - 1) * 34 - 16;
  drawLinkIcon(context, iconX, iconY, 20);
  const linkTextHeight = linkLines.length * 34;
  const linkRect = {
    x: padding + 136,
    y: linkTextY - 26,
    width: contentWidth - 136,
    height: linkTextHeight + 8,
  };

  y += linkTextHeight + 36;
  context.fillStyle = "#141414";
  context.font = "500 24px Arial";
  drawWrappedText(
    context,
    "Note: By confirming your presence, you will gain access to all event-related photos presentations, and exclusive content on JioAICloud.",
    padding,
    y,
    contentWidth,
    36,
  );

  const blob = await canvasToJpgBlob(canvas);

  return {
    blob,
    linkRect,
    logicalSize: {
      width,
      height,
    },
  };
};

const getJpegDimensions = (bytes) => {
  let offset = 2;

  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) break;

    const marker = bytes[offset + 1];
    const length = (bytes[offset + 2] << 8) + bytes[offset + 3];

    if (marker >= 0xc0 && marker <= 0xc3) {
      return {
        height: (bytes[offset + 5] << 8) + bytes[offset + 6],
        width: (bytes[offset + 7] << 8) + bytes[offset + 8],
      };
    }

    offset += 2 + length;
  }

  return { width: 1, height: 1 };
};

const createPdfFromJpgBlob = async (
  jpgBlob,
  linkUrl,
  linkRect,
  logicalSize,
) => {
  const encoder = new TextEncoder();
  const jpgBytes = new Uint8Array(await jpgBlob.arrayBuffer());
  const { width, height } = getJpegDimensions(jpgBytes);
  const imageRatio = width / height;
  const maxWidth = A4_WIDTH - 48;
  const maxHeight = A4_HEIGHT - 48;
  let drawWidth = maxWidth;
  let drawHeight = drawWidth / imageRatio;

  if (drawHeight > maxHeight) {
    drawHeight = maxHeight;
    drawWidth = drawHeight * imageRatio;
  }

  const x = (A4_WIDTH - drawWidth) / 2;
  const y = (A4_HEIGHT - drawHeight) / 2;
  const annotationObjectId = 6;
  const pageAnnotations =
    linkUrl && linkRect && logicalSize
      ? ` /Annots [${annotationObjectId} 0 R]`
      : "";
  const linkScaleX = drawWidth / logicalSize?.width;
  const linkScaleY = drawHeight / logicalSize?.height;
  const pdfLinkRect =
    linkUrl && linkRect && logicalSize
      ? [
        x + linkRect.x * linkScaleX,
        y + drawHeight - (linkRect.y + linkRect.height) * linkScaleY,
        x + (linkRect.x + linkRect.width) * linkScaleX,
        y + drawHeight - linkRect.y * linkScaleY,
      ]
      : null;
  const content = `q\n${drawWidth.toFixed(2)} 0 0 ${drawHeight.toFixed(
    2,
  )} ${x.toFixed(2)} ${y.toFixed(2)} cm\n/Im0 Do\nQ`;
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${A4_WIDTH} ${A4_HEIGHT}] /Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R${pageAnnotations} >>`,
    `<< /Length ${encoder.encode(content).length} >>\nstream\n${content}\nendstream`,
    {
      header: `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpgBytes.length} >>\nstream\n`,
      stream: jpgBytes,
      footer: "\nendstream",
    },
  ];

  if (pdfLinkRect) {
    objects.push(
      `<< /Type /Annot /Subtype /Link /Rect [${pdfLinkRect
        .map((value) => value.toFixed(2))
        .join(" ")}] /Border [0 0 0] /A << /S /URI /URI (${escapePdfString(
          linkUrl,
        )}) >> >>`,
    );
  }
  const parts = ["%PDF-1.4\n"];
  const offsets = [0];
  let position = encoder.encode(parts[0]).length;

  objects.forEach((object, index) => {
    offsets.push(position);
    const objectHeader = `${index + 1} 0 obj\n`;
    parts.push(objectHeader);
    position += encoder.encode(objectHeader).length;

    if (typeof object === "string") {
      const objectBody = `${object}\nendobj\n`;
      parts.push(objectBody);
      position += encoder.encode(objectBody).length;
      return;
    }

    parts.push(object.header, object.stream, object.footer, "\nendobj\n");
    position += encoder.encode(object.header).length;
    position += object.stream.length;
    position += encoder.encode(`${object.footer}\nendobj\n`).length;
  });

  const xrefOffset = position;
  const xref = [
    "xref",
    `0 ${objects.length + 1}`,
    "0000000000 65535 f ",
    ...offsets
      .slice(1)
      .map((offset) => `${String(offset).padStart(10, "0")} 00000 n `),
    "trailer",
    `<< /Size ${objects.length + 1} /Root 1 0 R >>`,
    "startxref",
    String(xrefOffset),
    "%%EOF",
  ].join("\n");

  parts.push(xref);

  return new Blob(parts, { type: "application/pdf" });
};

export default function CommonLayout(props) {
  const { id: eventId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [eventDetails, setEventDetails] = useState();
  const [eventDes, setEventDes] = useState();
  const [eventRecall, setEventRecall] = useState(true);
  const [previewModalCls, setPreviewModalCls] = useState(true);
  const [locationCoordinate, setLocationCoordinate] = useState(null);
  const [isAutoApproval, setIsAutoApproval] = useState(false);
  const [isAutoApprovalModalClose, setIsAutoApprovalModalClose] =
    useState(true);
  const isEventDetailPage = window.location.pathname
    .split("/")
    .join(" ")
    .includes("event-detail");

  const { sendRequest: getEventDetails, isLoading: eventDetailsLoading } =
    useApiHttp();
  const {
    sendRequest: updateDescription,
    isLoading: isUpdatingUserDescription,
  } = useApiHttp();
  const { sendRequest: bannerUpdate, isLoading: isUpdatingBanner } =
    useApiHttp();

  const { sendRequest: makeEventPublicApi, isLoading: isAutoApprovalLoading } =
    useApiHttp();

  const culumData = (data) => {
    dispatch(setSidebarDetails({ eventDetails: data?.data }));
    setEventDetails(data?.data);
    setEventDes(data?.data?.description);
    setLocationCoordinate({
      lat: parseFloat(data?.data?.locationLatitude),
      lng: parseFloat(data?.data?.locationLongitude),
    });
    setIsAutoApproval(data?.data?.isAutoApproval);
  };

  const { isLoaded } = useJsApiLoader({
    // id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  });

  useEffect(() => {
    getEventDetails(
      {
        url: `event/get-event/${eventId}`,
        method: "GET",
      },
      culumData,
      () => { },
      () => { },
    );
  }, [eventRecall, eventId]);

  return (
    <>
      {!eventDetailsLoading ? (
        <>
          {isEventDetailPage && (
            // <Container direction="column" gap={24} fullWidth >
            <div className={classes["wrapper"]}>
              {/* {eventDetails?.userRole === "ADMIN" && (
                // <InputToggle
                //   label="Public Event"
                //   checked={false}
                //   onChange={(e) => {
                //     setIsAutoApproval(false);
                //     setIsAutoApprovalModalClose(false);
                //   }}
                // />
                <FormControlLabel
                  checked={isAutoApproval}
                  control={<Switch />}
                  label={<Text text="Public Event" color="primary_grey_80" />}
                  labelPlacement="start"
                  onChange={() => setIsAutoApprovalModalClose(false)}
                />
              )} */}
              <EventBanner
                bannerUpdate={bannerUpdate}
                eventDate={eventDetails?.startDate}
                eventTime={eventDetails?.time}
                // bannerImage={
                //   eventDetails?.bannerimage
                //     ? baseToImg(eventDetails?.bannerimage)
                //     : ""
                // }
                bannerImage={
                  eventDetails?.bannerimage ? eventDetails?.bannerimage : ""
                }
                eventDescription={eventDes}
                eventId={eventId}
                eventRecall={() => {
                  setEventRecall((prev) => !prev);
                }}
                eventDetails={eventDetails}
                watermarkModalOpen={location?.state?.isWatermarkModalOpen}
              />

              <div
                className={classes["eventdetails-content"]}
                style={{
                  display: `${eventDetails?.userRole !== "PHOTOGRAPHER" ? "grid" : "flex"
                    }`,
                  gridTemplateColumns: "1fr 1fr 1fr",
                  // gridTemplateRows: "304px",
                  width: "100%",
                  justifyContent: `${eventDetails?.userRole !== "PHOTOGRAPHER"
                      ? ""
                      : "space-between"
                    }`,
                }}>
                <div
                  style={{
                    borderRadius: "10px",
                    height: "100%",
                    // boxShadow: " 0px 4px 16px 0px #00000014",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}>
                  <Container
                    direction="column"
                    gap={8}
                    flex={1}
                    justifyContent="space-between"
                    style={{ maxWidth: "584px" }}
                    fullWidth>
                    <Text
                      text="Event Location"
                      appearance="body-m-bold"
                      color="primary_grey_80"
                    />
                    <Container
                      fullWidth
                      direction="column"
                      justifyContent="space-between"
                      gap={4}
                      style={{
                        overflow: "hidden",
                        borderRadius: "16px",
                        border: "1px solid var(--color-primary-grey-40)",
                      }}>
                      <Container
                        direction="column"
                        justifyContent="space-between"
                        gap={"16px"}
                        fullWidth>
                        {isLoaded ? (
                          <GoogleMap
                            center={locationCoordinate}
                            zoom={15}
                            mapContainerStyle={{
                              width: `${eventDetails?.userRole !== "PHOTOGRAPHER"
                                  ? "100%"
                                  : "650px"
                                }`,
                              height: "224px",
                            }}
                            options={{
                              disableDefaultUI: true, // Disables all default controls
                              zoomControl: false, // Disables zoom control
                              streetViewControl: false, // Disables Street View control
                              mapTypeControl: false, // Disables map type control
                              fullscreenControl: false, // Disables fullscreen control
                              gestureHandling: "none", // Disables all user gestures
                            }}>
                            {locationCoordinate && (
                              <Marker position={locationCoordinate} />
                            )}
                          </GoogleMap>
                        ) : (
                          <Skeleton shape="rectangle" />
                        )}
                      </Container>
                      <Container
                        direction="column"
                        gap={8}
                        style={{ padding: "12px" }}>
                        <Text
                          text="Address"
                          appearance="body-m-bold"
                          color="primary_grey_80"
                        />
                        <Text
                          text={eventDetails?.locationName || "Address"}
                          appearance="body-m"
                        />
                      </Container>
                    </Container>
                  </Container>
                </div>
                <div>
                  <p className={classes["guides-card"]}>
                    {eventDetails?.userRole !== "PHOTOGRAPHER" ? (
                      <Text
                        text="Guides"
                        appearance="body-m-bold"
                        color="primary_grey_80"
                      />
                    ) : (
                      ""
                    )}
                  </p>
                  <div
                    className={classes["guides-content"]}
                    style={{
                      paddingInline: "40px",
                      paddingTop: "30px",
                      display: "flex",
                      flexDirection: "column",
                      // gap: "16px",
                      justifyContent: "space-between",
                      height: "354px",
                    }}>
                    {eventDetails?.userRole !== "PHOTOGRAPHER" ? (
                      <Button
                        iconLeft={"IcGroup"}
                        icon={"IcArrowNext"}
                        kind="secondary"
                        label="Invite Guests"
                        onClick={() => {
                          navigate(`/home/events/guests/${eventId}`, {
                            state: { addGuest: true },
                          });
                        }}
                        fullWidth
                      />
                    ) : (
                      ""
                    )}
                    <Button
                      iconLeft={"IcPhoto"}
                      icon={"IcArrowNext"}
                      kind="secondary"
                      label="Upload Photos"
                      onClick={() => {
                        navigate(`/home/events/albums/${eventId}`, {
                          state: { newFolder: true },
                        });
                      }}
                      fullWidth
                    />
                    {/* <div
                      onClick={() => {
                        navigate(`/home/events/guests/${eventId}`, {
                          state: { addGuest: true },
                        });
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                      className={classes["bg-shadow"]}
                    >
                      <Container
                        fullWidth
                        direction="row"
                        verticalAlign="center"
                      >
                        <Text
                          text="Go to users and start inviting guests"
                          appearance="body-m-bold"
                          color="primary-50"
                        />
                        <Icon ic={"IcArrowNext"} />
                      </Container>
                    </div> */}
                    {/* <div
                      onClick={() => {
                        navigate(`/home/events/albums/${eventId}`, {
                          state: { newFolder: true },
                        });
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                      className={classes["bg-shadow"]}
                    >
                      <Container
                        fullWidth
                        direction="row"
                        verticalAlign="center"
                      >
                        <Text
                          text="Go to photos and start uploading albums"
                          appearance="body-m-bold"
                          color="primary-50"
                        />
                        <Icon ic={"IcArrowNext"} />
                      </Container>
                    </div> */}
                    <Container
                      fullWidth
                      direction="row"
                      horizontalAlign="center"
                      style={{
                        alignSelf: "end",
                      }}>
                      {eventDetails?.userRole !== "PHOTOGRAPHER" ? (
                        <Button
                          label="Preview Invitation"
                          onClick={() => {
                            setPreviewModalCls(false);
                          }}
                          fullWidth
                        />
                      ) : (
                        ""
                      )}
                      <JdsModal
                        header={"Preview Invitation"}
                        close={previewModalCls}
                        onClose={() => {
                          setPreviewModalCls(true);
                        }}
                        size="m">
                        {!previewModalCls && (
                          <PreviewInvitation eventDetails={eventDetails} />
                        )}
                      </JdsModal>
                    </Container>
                  </div>
                </div>
                {eventDetails?.userRole !== "PHOTOGRAPHER" ? (
                  <div
                    style={{
                      borderRadius: "10px",
                      // boxShadow: " 0px 4px 16px 0px #00000014",
                    }}>
                    <Container
                      direction="column"
                      fullWidth
                      gap={8}
                      horizontalAlign="center">
                      <Container
                        style={{
                          width: "100% ",
                        }}
                        direction="row"
                        horizontalAlign="space-between">
                        <Text
                          text="QR Image"
                          appearance="body-m-bold"
                          color="primary_grey_80"
                        />
                        <span
                          onClick={() => {
                            downloadQrImage(
                              eventDetails?.qrCodeImage,
                              eventDetails,
                            );
                          }}
                          style={{
                            cursor: "pointer",
                            display: "flex",
                            gap: "5px",
                          }}>
                          <Text
                            text="Download"
                            appearance="body-m-bold"
                            color="primary-50"
                          />
                          <Icon ic={"IcDownload"} />
                        </span>
                      </Container>
                      <Box
                        sx={{
                          border: "1px solid var(--color-primary-grey-40)",
                          width: "100%",
                          textAlign: "center",
                          padding: "24px 8px",
                          // backgroundColor: "var(--color-primary-grey-20)",
                          borderRadius: "16px",
                          height: "344px",
                        }}>
                        <img
                          src={baseToImg(eventDetails?.qrCodeImage)}
                          alt="qr code"
                          style={{
                            // height: "100%",
                            width: "200px",
                          }}
                        />

                        <Container
                          horizontalAlign="center"
                          fullWidth
                          style={{ marginTop: "24px" }}>
                          <Text
                            text="Event Code:"
                            appearance="body-m-bold"
                            color="primary_grey_80"
                          />
                          <Text
                            text={eventDetails?.uniquePin}
                            appearance="body-m"
                            color="primary_50"
                          />
                        </Container>

                        <Container
                          horizontalAlign="center"
                          fullWidth
                          style={{ marginTop: "24px" }}>
                          <span
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              fontSize: "18px",
                              color: "var(--color-primary-grey-80)",
                              fontWeight: "700",
                            }}>
                            Event Link :
                          </span>
                          <span
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              cursor: "pointer",
                              gap: "4px",
                            }}
                            onClick={() => {
                              navigator.clipboard
                                .writeText(
                                  `${process.env.REACT_APP_JIOCLOUD_LANDING_URL}/events/sign-up?event_id=${eventDetails?.id}`,
                                )
                                .then(() => {
                                  Notifications.toast.success({
                                    id: "copy-link",
                                    title: "Link copied to clipboard",
                                  });
                                })
                                .catch((err) => { });
                            }}>
                            {/* <Text
                              text={
                                eventDetails?.name 
                               
                              }
                              appearance="body-m"
                              color="primary_50"
                              maxLines={1}
                            /> */}
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                fontSize: "18px",
                                color: "var(--color-primary-50)",
                                fontWeight: "500",
                                textOverflow: "ellipsis",
                                fontFamily: "var(--font-families-body-m)",
                              }}>
                              {eventDetails?.name}
                            </span>
                            <Icon ic={"IcLink"} size="m" />
                          </span>
                          {/* <span>{eventDetails?.name}</span> */}
                        </Container>
                      </Box>
                    </Container>
                  </div>
                ) : (
                  ""
                )}
              </div>
              {/* </Container> */}
            </div>
          )}
          <Box mt={isEventDetailPage && 6}>
            <Outlet context={[eventDetails]} />
          </Box>
        </>
      ) : (
        <PageLoader open={eventDetailsLoading} />
      )}

      <PageLoader open={isUpdatingUserDescription || isUpdatingBanner} />

      <JdsModal
        size="m"
        header={`Are you sure you want to make this event ${!isAutoApproval ? "public" : "private"
          } ?`}
        kind="dialog"
        primaryCTA={{
          title: "Okay",
        }}
        secondaryCTA={{
          title: "Cancel",
        }}
        close={isAutoApprovalModalClose}
        onClose={() => setIsAutoApprovalModalClose(true)}
        onPrimaryClick={() => {
          makeEventPublicApi(
            {
              url: "event/switch-auto-approval",
              method: "PATCH",
              body: {
                id: eventId,
                isAutoApproval: !isAutoApproval,
              },
            },
            (data) => { },
            (data) => {
              setIsAutoApprovalModalClose(true);
              Notifications.toast.success({
                id: "switch-approval",
                title: data?.message,
              });
              setIsAutoApproval((prev) => !prev);
            },
          );
        }}
        onSecondaryClick={() => setIsAutoApprovalModalClose(true)}
        primaryCtaDisabled={isAutoApprovalLoading}>
        <Text
          text={
            !isAutoApproval
              ? "All the registered users will get auto approval for this event!"
              : "The registered users have to be approved manually!"
          }
        />
      </JdsModal>
    </>
  );
}

const PreviewInvitation = ({ eventDetails }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadInvitation = async (format) => {
    if (isDownloading) return;

    setIsDownloading(true);

    try {
      const invitationExport = await createInvitationJpgBlob(eventDetails);

      if (format === "jpg") {
        downloadBlob(
          invitationExport.blob,
          getInvitationFileName(eventDetails?.name, "jpg"),
        );
        return;
      }

      const pdfBlob = await createPdfFromJpgBlob(
        invitationExport.blob,
        getEventRegistrationLink(eventDetails?.id),
        invitationExport.linkRect,
        invitationExport.logicalSize,
      );
      downloadBlob(pdfBlob, getInvitationFileName(eventDetails?.name, "pdf"));
    } catch (error) {
      Notifications.toast.error({
        id: "download-invitation-error",
        title: "Unable to download invitation. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <div className={classes["invitation-actions"]}>
        <Button
          kind="secondary"
          iconLeft="IcDownload"
          label="Download JPG"
          onClick={() => downloadInvitation("jpg")}
          disabled={isDownloading}
        />
        <Button
          kind="secondary"
          iconLeft="IcDownload"
          label="Download PDF"
          onClick={() => downloadInvitation("pdf")}
          disabled={isDownloading}
        />
      </div>
      <div className={classes["invitation-preview"]}>
        {/* <img src={invitationImg} /> */}
        <img
          alt={`${eventDetails?.name || "Event"} invitation banner`}
          className={classes["invitation-banner"]}
          src={
            eventDetails?.bannerimage
              ? addParamsURL(decryptUrl(eventDetails?.bannerimage), {
                size: "s",
              })
              : ""
          }
        />
        <p className={classes["invitation-title"]}>You're Invited!</p>
        <div>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "500",
            }}>{`We are thrilled to extend an exclusive invitation to you for ${eventDetails?.name} via JioAICloud, your personalized event companion. Your presence would add immense value to the event, and we genuinely hope you can join us.`}</p>
          <p style={{ fontSize: "16px", fontWeight: "700", marginTop: "10px" }}>
            Event Details:
          </p>
          <div
            style={{
              boxShadow:
                " rgba(0, 0, 0, 0.02) 0px 1px 3px 0px , rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
              padding: "15px",
              borderRadius: "10px",
              marginTop: "5px",
            }}>
            <table>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: "nowrap", width: "100px" }}>
                    Event Name
                  </td>
                  <td style={{ paddingLeft: "8px" }}> : {eventDetails.name}</td>
                </tr>
                {/* <tr>
                <td>Organized By</td>
                <td>balaji birthday</td>
              </tr> */}
                <tr>
                  <td>
                    {eventDetails?.endDate === eventDetails?.startDate
                      ? "Date "
                      : "From "}
                  </td>
                  <td style={{ paddingLeft: "8px" }}>
                    {" "}
                    : {eventDetails?.startDate}
                  </td>
                </tr>
                <tr>
                  <td>
                    {eventDetails?.endDate === eventDetails?.startDate
                      ? "Time "
                      : "To "}
                  </td>
                  <td style={{ paddingLeft: "8px" }}>
                    :{" "}
                    {eventDetails?.endDate === eventDetails?.startDate
                      ? eventDetails?.time
                      : eventDetails?.endDate}
                  </td>
                </tr>
                <tr>
                  <td>Venue</td>
                  <td style={{ paddingLeft: "8px" }}>
                    : {eventDetails?.locationName}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: "16px", fontWeight: "700", marginTop: "10px" }}>
            Register QR:
          </p>
          <div style={{ marginInline: "auto", width: "200px" }}>
            <img
              alt="Registration QR code"
              src={baseToImg(eventDetails?.qrCodeImage)}
              style={{ width: "200px" }}
            />
          </div>
          <Stack
            my={1}
            direction="row"
            gap={1}
            sx={{
              "& > a": {
                color: "var(--color-primary-50)",
                fontFamily: "var(--font-families-body-sbold)",
              },
              "& .text-atom": {
                whiteSpace: "nowrap",
              },
            }}>
            <Container
              horizontalAlign="center"
              fullWidth
              style={{ marginTop: "24px" }}>
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  fontSize: "18px",
                  color: "var(--color-primary-grey-80)",
                  fontWeight: "700",
                }}>
                Event Link:
              </span>
              <span
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                  gap: "4px",
                }}
                onClick={() => {
                  navigator.clipboard
                    .writeText(
                      `${process.env.REACT_APP_JIOCLOUD_LANDING_URL}/events/sign-up?event_id=${eventDetails?.id}`,
                    )
                    .then(() => {
                      Notifications.toast.success({
                        id: "copy-link",
                        title: "Link copied to clipboard",
                      });
                    })
                    .catch((err) => { });
                }}>
                {/* <Text
                              text={
                                eventDetails?.name 
                               
                              }
                              appearance="body-m"
                              color="primary_50"
                              maxLines={1}
                            /> */}
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    fontSize: "18px",
                    color: "var(--color-primary-50)",
                    fontWeight: "500",
                    textOverflow: "ellipsis",
                    fontFamily: "var(--font-families-body-m)",
                  }}>
                  {eventDetails?.name}
                </span>
                <Icon ic={"IcLink"} size="m" />
              </span>
              {/* <span>{eventDetails?.name}</span> */}
            </Container>
          </Stack>
          <p style={{ fontSize: "16px", fontWeight: "500" }}>
            Note: By confirming your presence, you will gain access to all
            event-related photos presentations, and exclusive content on
            JioAICloud.
          </p>
        </div>
      </div>
    </>
  );
};
